import { createFileRoute, Link, Outlet, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { myEntitiesQueryOptions } from "@/lib/queries";

export const Route = createFileRoute("/_auth/vendors/$vendorId")({
  component: VendorLayout,
});

function VendorLayout() {
  const { vendorId } = Route.useParams();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Construction pack gate — optimistically true on first paint
  const entitiesQuery = useQuery(myEntitiesQueryOptions());
  const currentEntity = entitiesQuery.data?.find((e) => e.is_current);
  const constructionEnabled = currentEntity?.construction_enabled ?? true;

  // Determine active tab
  const seg = pathname.split("/").pop() ?? "";
  const activeTab = (test: string) =>
    seg === test || (test === "detail" && (seg === vendorId || seg === ""));

  const tabCls = (active: boolean) =>
    `px-3 py-2.5 text-sm border-b-2 font-medium whitespace-nowrap ${
      active
        ? "border-blue-500 text-blue-600 dark:text-blue-400"
        : "border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
    }`;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)]">
      <div className="bg-white dark:bg-gray-900 border-b border-slate-200 dark:border-gray-800 px-6 flex items-center gap-1 overflow-x-auto">
        <Link
          to="/vendors/$vendorId"
          params={{ vendorId }}
          className={tabCls(activeTab("detail"))}
        >
          Detail
        </Link>
        <Link
          to="/vendors/$vendorId/qualification"
          params={{ vendorId }}
          className={tabCls(activeTab("qualification"))}
        >
          Qualification
        </Link>
        {constructionEnabled && (
          <>
            <Link
              to="/vendors/$vendorId/pos"
              params={{ vendorId }}
              className={tabCls(activeTab("pos"))}
            >
              Purchase Orders
            </Link>
            <Link
              to="/vendors/$vendorId/subcontracts"
              params={{ vendorId }}
              className={tabCls(activeTab("subcontracts"))}
            >
              Subcontracts
            </Link>
            <Link
              to="/vendors/$vendorId/lien-waivers"
              params={{ vendorId }}
              className={tabCls(activeTab("lien-waivers"))}
            >
              Lien Waivers
            </Link>
          </>
        )}
        <Link
          to="/vendors/$vendorId/addresses"
          params={{ vendorId }}
          className={tabCls(activeTab("addresses"))}
        >
          Addresses
        </Link>
        <Link
          to="/vendors/$vendorId/payments"
          params={{ vendorId }}
          className={tabCls(activeTab("payments"))}
        >
          Payments
        </Link>
        <Link
          to="/vendors/$vendorId/autopilot"
          params={{ vendorId }}
          className={tabCls(activeTab("autopilot"))}
        >
          Autopilot
        </Link>
        <Link
          to="/vendors/$vendorId/activity"
          params={{ vendorId }}
          className={tabCls(activeTab("activity"))}
        >
          Activity
        </Link>
      </div>
      <div className="flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
